// src/pages/UsersPage.js
import { useState, useEffect } from 'react';
import api from '../utils/api';
import { USER_ROLES, getLabelFor } from '../utils/constants';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', password:'', role:'viewer' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/users').then(setUsers).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const created = await api.post('/users', form);
      setUsers(u => [...u, created]);
      setShowForm(false);
      setForm({ name:'', email:'', password:'', role:'viewer' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Users</div>
          <div style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginTop:'0.2rem' }}>Manage team access</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          <PlusIcon /> Add User
        </button>
      </div>

      <div className="card" style={{ padding:0 }}>
        {loading ? (
          <div style={{ padding:'3rem', textAlign:'center', color:'var(--text-muted)' }}>Loading…</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last Login</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
                      <div style={{ width:30, height:30, borderRadius:'50%', background:'var(--accent-dim)', border:'1px solid var(--accent)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem', fontWeight:700, color:'var(--accent)', flexShrink:0 }}>
                        {u.name.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontWeight:500 }}>{u.name}</span>
                    </div>
                  </td>
                  <td style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>{u.email}</td>
                  <td>
                    <span className="badge" style={{ background:'var(--bg-hover)', color:'var(--text-secondary)' }}>
                      {getLabelFor(USER_ROLES, u.role)}
                    </span>
                  </td>
                  <td style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('en-CA') : 'Never'}
                  </td>
                  <td>
                    <span style={{ fontSize:'0.75rem', fontWeight:600, color: u.is_active ? 'var(--green)' : 'var(--red)' }}>
                      {u.is_active ? '● Active' : '○ Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Add User</span>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                {error && <div style={{ background:'var(--red-dim)', border:'1px solid var(--red)', borderRadius:'var(--radius-sm)', padding:'0.75rem', fontSize:'0.85rem', color:'var(--red)' }}>{error}</div>}
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Jane Smith" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@coastalmining.ca" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Min. 8 characters" required minLength={8} />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select value={form.role} onChange={e => set('role', e.target.value)}>
                    {USER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:'0.25rem' }}>
                    Admin: full access. Manager: create/edit. Viewer: read-only.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function PlusIcon() { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
